/**
 * UnitDetailPage — route `/properties/:id/units/:unitId` (PMS portal).
 *
 * SCRUM-180 (P4) — Unit detail shell.
 *
 * Replaces the SCRUM-168 P0 NotConnectedYet placeholder with the
 * bundle's canonical Unit detail layout (per
 * `~/Desktop/design_handoff_properties/UNIT_DETAIL_SPEC.md`):
 *
 *   - Hero card (status badges row + name + property subtitle +
 *     action buttons + WD/WE base+floor stat strip)
 *   - Composition card (spaces that compose this unit)
 *   - Channels card (Airbnb / VRBO / BDC + Instant book DetailRows)
 *   - SIM conflict map (empty-state body — BE has no Unit/Space
 *     entities yet, so the diagram surface is wrapped in
 *     `<NotConnectedYet>` instead of a real `<SimDiagram>`)
 *
 * The page renders the chrome with the property's name as the
 * subtitle (sourced from `useProperty(propertyId)`) and the unitId
 * fragment as the heading. The body is entirely shell — Property
 * IS the unit today per the SCRUM-167 epic matrix. When the BE
 * grows a real Unit entity, this page's heading swaps from "{id}" to
 * the unit name, and the cards point at the wired DTO; chrome stays
 * put.
 *
 * Gated by `property.read` (read access to the parent property
 * implies read access to its units).
 */

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Icon } from '@sparta/ui-kit';
import { useProperty } from '../api/use-property-detail.js';
import { Stat } from '../_components/Stat.js';
import { UnitStatus } from '../_components/UnitStatus.js';
import { NotConnectedYet } from '../../../entities/not-connected-yet/index.js';
import { SimDiagram, MOCK_SIM_DEMO } from '../_components/SimDiagram.js';

export function UnitDetailPage(): React.ReactElement {
  const { id, unitId } = useParams<{ id: string; unitId: string }>();
  const navigate = useNavigate();
  const propertyResult = useProperty(id);

  const propertyName =
    propertyResult.status === 'ready' ? propertyResult.data.name : 'Property';
  const propertyCity =
    propertyResult.status === 'ready'
      ? propertyResult.data.address.city
      : '';
  // Unit status mirrors the property's status today (Property IS the
  // unit). When real units land, the status comes from the unit DTO.
  const unitStatus =
    propertyResult.status === 'ready'
      ? propertyResult.data.status
      : { op: 'active' as const, av: 'bookable' as const };

  const fragment =
    unitId !== undefined && unitId.length > 12
      ? `${unitId.slice(0, 8)}…`
      : unitId ?? 'Unit';

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-12 sm:py-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(`/properties/${id ?? ''}`)}
        className="mb-3"
      >
        <Icon name="chevLeft" size={14} aria-hidden />
        <span className="ml-2">{propertyName}</span>
      </Button>

      {/* Hero card — radial wash + status badges + name + property +
          action row + stat strip. Mirrors PropertyHero's recipe. */}
      <section
        aria-labelledby="unit-hero-title"
        className="relative rounded-2xl overflow-hidden mb-4"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 700px 280px at 92% -10%, color-mix(in oklab, var(--brand-500) 14%, transparent), transparent 70%)',
          }}
        />
        <div className="relative px-6 pt-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <UnitStatus op={unitStatus.op} av={unitStatus.av} />
                {/* Sleeps / space-count are unit attributes the BE has no
                    entity for yet (SCRUM-188). Show an honest "—" rather
                    than a fabricated count (Simon review #5). */}
                <Badge variant="neutral" size="sm">
                  Sleeps —
                </Badge>
              </div>
              <h1
                id="unit-hero-title"
                className="m-0 text-2xl font-semibold"
                style={{
                  color: 'var(--fg-primary)',
                  letterSpacing: '-0.025em',
                }}
              >
                Unit {fragment}
              </h1>
              <p
                className="m-0 text-sm"
                style={{ color: 'var(--fg-secondary)' }}
              >
                {propertyName}
                {propertyCity !== '' ? ` · ${propertyCity}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  alert(
                    'Preview listing — coming soon (SCRUM-167 follow-up).',
                  );
                }}
              >
                <Icon name="eye" size={14} aria-hidden />
                <span className="ml-2">Preview listing</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  alert('Edit status — open from parent property detail.');
                }}
              >
                <Icon name="settings" size={14} aria-hidden />
                <span className="ml-2">Edit status</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  alert(
                    'Edit unit — not connected yet (awaiting SCRUM-188 Spaces+Units BE entity).',
                  );
                }}
              >
                <Icon name="pencil" size={14} aria-hidden />
                <span className="ml-2">Edit unit</span>
              </Button>
            </div>
          </div>
          {/* Stat strip — 2×2 grid on mobile (per
              IMPLEMENTATION_CHECKLIST), 4-up flex on sm+. The third
              cell (WD floor) suppresses its left border at mobile
              because it starts the second grid row, then reinstates
              the divider at sm+ when it sits inside the flex row. */}
          <div
            className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap"
            style={{ borderTop: '1px solid var(--border-subtle)' }}
          >
            <Stat label="WD base" value="—" />
            <Stat label="WE base" value="—" border />
            <Stat
              label="WD floor"
              value="—"
              border
              className="!border-l-0 sm:!border-l"
            />
            <Stat label="WE floor" value="—" border />
          </div>
        </div>
      </section>

      {/* Two-column section: Composition + Channels.
          Both bodies are shell — BE has no Unit/Space/channels yet. */}
      <div className="grid gap-4 md:grid-cols-2 mb-4">
        <Card padding="md">
          <h2
            className="m-0 mb-1 text-base font-semibold"
            style={{ color: 'var(--fg-primary)' }}
          >
            Composition
          </h2>
          <p
            className="m-0 mb-3 text-xs"
            style={{ color: 'var(--fg-secondary)' }}
          >
            The spaces that compose this unit.
          </p>
          <NotConnectedYet surface="Composition" ticket="SCRUM-188" />
        </Card>

        <Card padding="md">
          <h2
            className="m-0 mb-1 text-base font-semibold"
            style={{ color: 'var(--fg-primary)' }}
          >
            Channels
          </h2>
          {/* OTA sync + instant-book state would read as real operational
              data, but the BE exposes no channel fields or sync endpoint
              yet — static "not synced" / "Off" rows are fake state (Simon
              review #5). NotConnectedYet keeps it honest until SCRUM-187. */}
          <NotConnectedYet surface="Channels" ticket="SCRUM-187" />
        </Card>
      </div>

      {/* SIM conflict map. Renders against bundle mock data with the
          unitId-derived first unit highlighted. When BE units land,
          the call site passes real (spaces, units) + the route's
          unitId; the diagram contract is unchanged. */}
      <Card padding="md">
        <div className="mb-1 flex items-center gap-2">
          <h2
            className="m-0 text-base font-semibold"
            style={{ color: 'var(--fg-primary)' }}
          >
            SIM conflict map
          </h2>
          {/* Unmistakably demo: the diagram renders sample units, not this
              property's data (Simon review #5). */}
          <Badge variant="neutral" size="sm">
            Demo
          </Badge>
        </div>
        <p
          className="m-0 mb-3 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Sample layout showing how shared spaces block other units — not
          this property&rsquo;s data.
        </p>
        <SimDiagram
          {...MOCK_SIM_DEMO}
          highlightUnitId={MOCK_SIM_DEMO.units[0]?.id}
        />
        <p
          className="mt-3 text-xs italic"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Demo data — wired when SCRUM-188 lands Spaces / Units on the BE.
        </p>
      </Card>
    </div>
  );
}
